<template>
  <div class="list-page">
    <div class="detail-banner">
      <div class="container">
        <h1>Featured Products</h1>
        <p class="pd-banner-sub">Curated solutions to power your global market entry and expansion.</p>
      </div>
    </div>
    <section class="section">
      <div class="container">
        <!-- 一级分类 -->
        <div class="cat-block" v-if="firstTypes.length">
          <div class="cat-head">
            <span class="cat-label">Category</span>
          </div>
          <div class="cat-tabs">
            <button
              v-for="t in firstTypes"
              :key="t.id"
              class="cat-tab"
              :class="{ active: activeFirstId === t.id }"
              @click="selectFirst(t.id)"
            >{{ t.name }}</button>
          </div>
        </div>
        <div class="empty-block" v-else-if="!typeLoading">No product categories yet</div>
        <div class="empty-block" v-else>Loading…</div>

        <!-- 二级分类（选了一级后展示） -->
        <div class="cat-block" v-if="activeFirstId && secondTypes.length">
          <div class="cat-head">
            <span class="cat-label">Subcategory</span>
          </div>
          <div class="cat-tabs">
            <button
              class="cat-tab"
              :class="{ active: activeSecondId === '' }"
              @click="selectSecond('')"
            >All</button>
            <button
              v-for="s in secondTypes"
              :key="s.id"
              class="cat-tab"
              :class="{ active: activeSecondId === s.id }"
              @click="selectSecond(s.id)"
            >{{ s.name }}</button>
          </div>
        </div>

        <!-- 三级分类（选了二级后展示） -->
        <div class="cat-block" v-if="activeSecondId && thirdTypes.length">
          <div class="cat-head">
            <span class="cat-label">Type</span>
          </div>
          <div class="cat-tabs">
            <button
              class="cat-tab"
              :class="{ active: activeThirdId === '' }"
              @click="selectThird('')"
            >All</button>
            <button
              v-for="th in thirdTypes"
              :key="th.id"
              class="cat-tab"
              :class="{ active: activeThirdId === th.id }"
              @click="selectThird(th.id)"
            >{{ th.name }}</button>
          </div>
        </div>

        <!-- 当前筛选路径 + 数量 -->
        <div class="block-title" v-if="activeFirstId">
          <span class="bt-main">{{ activeFirstName }}</span>
          <span class="bt-sub" v-if="activeSecondName"> / {{ activeSecondName }}</span>
          <span class="bt-sub" v-if="activeThirdName"> / {{ activeThirdName }}</span>
          <span class="bt-count">{{ productTotal }} items</span>
        </div>

        <!-- 产品网格 -->
        <div class="card-grid" v-if="productRows.length">
          <div
            v-for="item in productRows"
            :key="item.id"
            class="card"
            @click="$router.push('/product/' + item.id)"
          >
            <div class="card-cover">
              <span class="cover-tag" v-if="item.category">{{ item.category }}</span>
              <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
              <span v-else class="cover-letter">{{ item.name.slice(0, 1) }}</span>
            </div>
            <div class="card-body">
              <div class="card-title">{{ item.name }}</div>
              <div class="card-desc">{{ item.summary }}</div>
              <div class="card-tags" v-if="item.subCategory || item.thirdCategory">
                <span class="card-tag" v-if="item.subCategory">{{ item.subCategory }}</span>
                <span class="card-tag" v-if="item.thirdCategory">{{ item.thirdCategory }}</span>
              </div>
              <div class="card-source" v-if="item.sourceCountry">Source: {{ item.sourceCountry }}</div>
              <div class="card-detail-btn">View Details</div>
            </div>
          </div>
        </div>

        <div class="empty-block" v-else-if="productLoading">Loading…</div>
        <div class="empty-block" v-else-if="activeFirstId">No products in this category</div>
        <div class="empty-block" v-else>Please select a product category above</div>

        <!-- 产品分页（服务端分页） -->
        <div class="pager" v-if="productTotalPages > 1">
          <button class="pg-btn" :disabled="productPage <= 1" @click="goProductPage(productPage - 1)">‹ Prev</button>
          <button
            v-for="p in productPages"
            :key="p"
            class="pg-num"
            :class="{ active: p === productPage }"
            @click="goProductPage(p)"
          >{{ p }}</button>
          <button class="pg-btn" :disabled="productPage >= productTotalPages" @click="goProductPage(productPage + 1)">Next ›</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  fetchProductFirstTypes,
  fetchProductTypeTree,
  fetchProductsByType
} from '../services/home'

export default {
  name: 'Products',
  data () {
    return {
      // 一级类型
      firstTypes: [],
      typeLoading: true,
      activeFirstId: '',
      // 二级类型（当前一级下的二级）
      secondTypes: [],
      activeSecondId: '',
      // 三级类型（当前二级下的三级）
      thirdTypes: [],
      activeThirdId: '',
      // 产品列表（按三级联动筛选，服务端分页）
      productRows: [],
      productTotal: 0,
      productPage: 1,
      productPageSize: 8,
      productLoading: true
    }
  },
  computed: {
    activeFirstName () {
      const t = this.firstTypes.find(x => x.id === this.activeFirstId)
      return t ? t.name : ''
    },
    activeSecondName () {
      if (!this.activeSecondId) return ''
      const s = this.secondTypes.find(x => x.id === this.activeSecondId)
      return s ? s.name : ''
    },
    activeThirdName () {
      if (!this.activeThirdId) return ''
      const t = this.thirdTypes.find(x => x.id === this.activeThirdId)
      return t ? t.name : ''
    },
    productTotalPages () {
      return Math.max(1, Math.ceil(this.productTotal / this.productPageSize))
    },
    productPages () {
      return this.buildPages(this.productPage, this.productTotalPages)
    }
  },
  created () {
    this.loadFirstTypes()
  },
  methods: {
    buildPages (cur, tp) {
      if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1)
      const set = new Set([1, tp, cur, cur - 1, cur + 1, cur - 2, cur + 2])
      const arr = [...set].filter(p => p >= 1 && p <= tp).sort((a, b) => a - b)
      const out = []
      let prev = 0
      for (const p of arr) {
        if (p - prev > 1) out.push('...')
        out.push(p)
        prev = p
      }
      return out
    },
    async loadFirstTypes () {
      this.typeLoading = true
      try {
        const res = await fetchProductFirstTypes({ pageNum: 1, pageSize: 100 })
        this.firstTypes = res.rows || []
        if (this.firstTypes.length) {
          this.selectFirst(this.firstTypes[0].id)
        }
      } catch (e) {
        this.firstTypes = []
        this.$toast.show({ message: (e && e.message) || 'Failed to load categories', type: 'error' })
      } finally {
        this.typeLoading = false
      }
    },
    async selectFirst (id) {
      if (this.activeFirstId === id) return
      this.activeFirstId = id
      this.activeSecondId = ''
      this.activeThirdId = ''
      this.thirdTypes = []
      this.productPage = 1
      // 加载该一级下的二三级树，并拉取该一级产品
      await this.loadTree(id)
      this.loadProducts()
    },
    async loadTree (id) {
      try {
        const tree = await fetchProductTypeTree(id)
        this.secondTypes = tree.children || []
      } catch (e) {
        this.secondTypes = []
      }
    },
    selectSecond (id) {
      if (this.activeSecondId === id) return
      this.activeSecondId = id
      this.activeThirdId = ''
      this.thirdTypes = []
      // 选择了具体二级时，展开其下的三级
      if (id) {
        const sec = this.secondTypes.find(x => x.id === id)
        this.thirdTypes = (sec && sec.children) || []
      }
      this.productPage = 1
      this.loadProducts()
    },
    selectThird (id) {
      if (this.activeThirdId === id) return
      this.activeThirdId = id
      this.productPage = 1
      this.loadProducts()
    },
    async loadProducts () {
      if (!this.activeFirstId) return
      this.productLoading = true
      try {
        const res = await fetchProductsByType({
          typeId: this.activeFirstId,
          secondTypeId: this.activeSecondId || undefined,
          thirdTypeId: this.activeThirdId || undefined,
          pageNum: this.productPage,
          pageSize: this.productPageSize
        })
        this.productRows = res.rows || []
        this.productTotal = res.total || 0
      } catch (e) {
        this.productRows = []
        this.productTotal = 0
        this.$toast.show({ message: (e && e.message) || 'Failed to load products', type: 'error' })
      } finally {
        this.productLoading = false
      }
    },
    goProductPage (p) {
      if (p < 1 || p > this.productTotalPages || p === this.productPage) return
      this.productPage = p
      this.loadProducts()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onImgError (e) {
      e.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
/* 蓝白主题变量（roadlinkglobal 全局调色板，组件内自包含，避免依赖外部 global.css） */
.list-page {
  --primary: #265fff;
  --primary-dark: #1a3a5f;
  --primary-light: #4dabf7;
  --blue-soft: #eaf0fe;
  --blue-soft-2: #f4f7fe;
  --text-main: #1f2a44;
  --text-sub: #5b6b8c;
  --border: #e6ebf5;
  --radius: 14px;
  --shadow: 0 6px 22px rgba(38, 95, 255, 0.08);
  --shadow-hover: 0 10px 30px rgba(38, 95, 255, 0.16);
  background: #f5f7fa;
  min-height: 100vh;
}

/* 顶部 banner：与首页 hero section 同款（home_banner.jpg 背景 + 居中 + 100px 内边距，无渐变叠加） */
.detail-banner {
  background: url(~@/assets/home_banner.jpg);
  background-size: cover;
  background-position: center;
  color: #fff;
  padding: 100px 0;
  text-align: center;
}
.detail-banner .container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.detail-banner h1 {
  font-size: 40px;
  font-weight: 800;
  margin: 0;
  line-height: 1.2;
}
.detail-banner .pd-banner-sub {
  margin: 0 auto;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.6;
  max-width: 700px;
}
@media (max-width: 768px) {
  .detail-banner { padding: 48px 0; }
  .detail-banner h1 { font-size: 28px; }
  .detail-banner .pd-banner-sub { font-size: 14px; }
}
@media (max-width: 576px) {
  .detail-banner h1 { font-size: 24px; }
  .detail-banner .pd-banner-sub { font-size: 13px; }
}

/* 区块 */
.section {
  padding: 40px 0 56px;
}

/* 分类联动面板（一级 / 二级 / 三级） */
.cat-block {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px 22px;
  box-shadow: var(--shadow);
  margin-bottom: 18px;
}
.cat-head {
  margin-bottom: 12px;
}
.cat-label {
  font-size: 14px;
  font-weight: 800;
  color: var(--text-main);
  position: relative;
  padding-left: 14px;
}
.cat-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 2px;
  width: 4px;
  border-radius: 3px;
  background: linear-gradient(180deg, var(--primary-light), var(--primary));
}
.cat-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.cat-tab {
  border: 1px solid var(--border);
  background: var(--blue-soft-2);
  color: var(--text-main);
  padding: 8px 18px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.22s ease;
}
.cat-tab:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.cat-tab.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

/* 当前筛选路径标题 */
.block-title {
  margin: 14px 0 22px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.bt-main {
  font-size: 19px;
  font-weight: 800;
  color: var(--text-main);
}
.bt-sub {
  font-size: 15px;
  font-weight: 700;
  color: var(--primary);
}
.bt-count {
  margin-left: auto;
  font-size: 13px;
  color: var(--text-sub);
}

/* 产品卡片网格（列表页响应式：大屏 4 列，平板 2 列，手机 1 列） */
.list-page .card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
}
@media (max-width: 900px) {
  .list-page .card-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .list-page .card-grid { grid-template-columns: 1fr; }
}

.card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}
.card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-hover);
}
.card-cover {
  height: 170px;
  /* 白色背景：图片完整等比展示，留白处为纯白 */
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
  font-size: 30px;
  font-weight: 800;
  position: relative;
  overflow: hidden;
}
.card-cover img {
  width: 100%;
  height: 100%;
  /* 等比例完整展示：contain 不裁剪，整张图按比例缩放居中 */
  object-fit: contain;
  object-position: center;
  display: block;
  padding: 10px;
  box-sizing: border-box;
}
.card-cover .cover-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(20, 30, 60, 0.82);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
}
.card-cover .cover-letter {
  font-size: 30px;
  font-weight: 800;
  color: var(--primary);
}
.card-body {
  padding: 16px 18px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.card-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--text-main);
  margin-bottom: 8px;
}
.card-desc {
  font-size: 13.5px;
  color: var(--text-sub);
  line-height: 1.7;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0;
}
.card-tag {
  background: var(--blue-soft);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 14px;
}
.card-source {
  margin-top: 12px;
  font-size: 12.5px;
  color: var(--primary);
  font-weight: 600;
}
.card-detail-btn {
  margin-top: 14px;
  align-self: center;
  background: var(--primary);
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  padding: 8px 26px;
  border-radius: 20px;
  transition: background 0.25s;
}
.card:hover .card-detail-btn {
  background: var(--primary-dark);
}

/* 分页器 */
.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
  flex-wrap: wrap;
}
.pg-btn,
.pg-num {
  min-width: 38px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text-main);
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.pg-btn:hover:not(:disabled),
.pg-num:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.pg-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.pg-num.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.pager :not(button) {
  color: var(--text-sub);
  padding: 0 4px;
}
.empty-block {
  text-align: center;
  color: var(--text-sub);
  padding: 40px 0;
}
</style>
